import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../services/api';
import { attestOnce } from '../services/attestation';

// Types
export interface User {
    user_id: string;
    username: string;
    email: string;
    full_name?: string;
    role: string;
    avatar_url?: string;
    bio?: string;
    timezone: string;
    preferences?: Record<string, any>;
    email_notifications: boolean;
    last_login_at?: string;
    allowed_modules: string[] | null;  // null = all access (admin or no restrictions set)
    allowed_projects: string[] | null; // null = all access (admin or no restrictions set)
    feature_flags?: string[];          // explicit opt-in flags (e.g. "noise_filter") — never implied by null allowed_modules
    onboarding_completed: boolean;
    feedback_submitted: boolean;
    tos_accepted_at?: string | null;
    tos_version?: string | null;
    company_id?: string;
    user_type?: string;  // 'individual' | 'company_admin' | 'company_member'
    tier?: string;       // 'trial' | 'growth' | 'pro' | 'enterprise'
    plan?: string;       // same values as tier, aliased for convenience
    email_verified?: boolean;
}

export interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (username: string, password: string) => Promise<User>;
    logout: () => void;
    register: (data: RegisterData) => Promise<{ checkout_url?: string }>;
    updateProfile: (data: ProfileUpdateData) => Promise<void>;
    refreshToken: () => Promise<boolean>;
}

interface RegisterData {
    username: string;
    email: string;
    password: string;
    full_name?: string;
    tos_version?: string;
    referral_code?: string;
    timezone?: string;
}

interface ProfileUpdateData {
    full_name?: string;
    avatar_url?: string;
    bio?: string;
    timezone?: string;
    preferences?: Record<string, any>;
    email_notifications?: boolean;
}

interface AuthProviderProps {
    children: ReactNode;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    login: async () => { throw new Error('AuthProvider not mounted'); },
    logout: () => {},
    register: async () => ({}),
    updateProfile: async () => {},
    refreshToken: async () => false,
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Get tokens from localStorage
    const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
    const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

    // Store tokens
    const setTokens = (accessToken: string, refreshToken: string) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    };

    // Clear tokens
    const clearTokens = () => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    };

    // Single-flight guard — if multiple requests fail with 401 simultaneously,
    // they all await the same refresh rather than racing on token rotation.
    let _refreshPromise: Promise<boolean> | null = null;

    // Fetch current user profile
    const fetchUser = useCallback(async () => {
        // Part 4: Skip auth check on public pages — ensures /login is always
        // reachable even when tokens are expired and AuthContext would otherwise loop.
        if (['/login', '/register', '/oauth-callback'].includes(window.location.pathname)) {
            setUser(null);
            setIsLoading(false);
            return;
        }

        const token = getAccessToken();
        if (!token) {
            setUser(null);
            setIsLoading(false);
            return;
        }

        try {
            const response = await apiClient.get('/auth/me');
            setUser(response.data);
        } catch (error: any) {
            if (error.response?.status === 401) {
                const refreshed = await refreshToken();
                if (!refreshed) {
                    clearTokens();
                    setUser(null);
                }
            } else {
                console.error('Error fetching user:', error);
                setUser(null);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Refresh access token
    const refreshToken = async (): Promise<boolean> => {
        const refresh = getRefreshToken();
        if (!refresh) {
            return false;
        }

        try {
            const response = await apiClient.post('/auth/refresh', null, {
                params: { refresh_token: refresh }
            });
            const { access_token, refresh_token: newRefresh } = response.data;
            setTokens(access_token, newRefresh);

            const userResponse = await apiClient.get('/auth/me');
            setUser(userResponse.data);
            return true;
        } catch (error) {
            console.error('Token refresh failed:', error);
            clearTokens();
            setUser(null);
            return false;
        }
    };

    // Part 2: single-flight wrapper — concurrent 401s share one refresh attempt
    const refreshTokenSingleFlight = (): Promise<boolean> => {
        if (!_refreshPromise) {
            _refreshPromise = refreshToken().finally(() => { _refreshPromise = null; });
        }
        return _refreshPromise;
    };

    // Login — returns the logged-in user so callers can act on permissions immediately
    const login = async (username: string, password: string): Promise<User> => {
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        const response = await apiClient.post('/auth/login', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });

        const { access_token, refresh_token } = response.data;
        setTokens(access_token, refresh_token);

        // Fetch user profile (includes allowed_modules / allowed_projects)
        const userResponse = await apiClient.get('/auth/me');
        setUser(userResponse.data);

        // Pre-resolve CRM routing mode in the background (non-blocking).
        // By the time the user navigates to the CRM module, the decision
        // (direct vs proxy) is already cached in sessionStorage.
        fetch('/crm/_sso_resolve', {
            headers: { Authorization: `Bearer ${access_token}` },
        }).then(r => r.json()).then(data => {
            if (data.mode === 'direct') {
                sessionStorage.setItem('crm_mode', 'direct');
                sessionStorage.setItem('crm_url', data.crm_url);
            } else {
                sessionStorage.setItem('crm_mode', 'proxy');
            }
        }).catch(() => {
            sessionStorage.setItem('crm_mode', 'proxy');
        });

        return userResponse.data as User;
    };

    // Logout
    const logout = () => {
        // Preserve pre-login preferences that should survive across user sessions
        const theme = localStorage.getItem('theme');
        const ref = localStorage.getItem('aime_ref');

        // Full reset — clears all tokens, project state, UI state, CRM state,
        // billing state, and any other cross-user contamination risk
        localStorage.clear();
        sessionStorage.clear();

        // Restore non-user-specific preferences
        if (theme) localStorage.setItem('theme', theme);
        if (ref) localStorage.setItem('aime_ref', ref);

        setUser(null);
    };

    // Register — creates account then auto-logs in
    const register = async (data: RegisterData): Promise<{ checkout_url?: string }> => {
        const regRes = await apiClient.post('/auth/register', data);
        const { checkout_url } = regRes.data || {};

        // Auto-login immediately after registration
        const formData = new URLSearchParams();
        formData.append('username', data.username);
        formData.append('password', data.password);
        const loginRes = await apiClient.post('/auth/login', formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        const { access_token, refresh_token } = loginRes.data;
        setTokens(access_token, refresh_token);
        const userResponse = await apiClient.get('/auth/me');
        setUser(userResponse.data);

        return { checkout_url };
    };

    // Update profile
    const updateProfile = async (data: ProfileUpdateData) => {
        const response = await apiClient.put('/auth/me', data);
        setUser(response.data);
    };

    // Initialize auth state on mount
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    // Anti-scraping attestation: fire-and-forget, once per tab session,
    // only after authentication (so the request carries the JWT).
    // attestOnce() swallows all errors — it can never affect UX.
    useEffect(() => {
        if (user) {
            attestOnce();
        }
    }, [user]);

    // Set up axios interceptor for auth header
    useEffect(() => {
        const requestInterceptor = apiClient.interceptors.request.use(
            (config) => {
                const token = getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = apiClient.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Part 1: exclude /auth/refresh from retry logic — root cause of the
                // infinite recursion. Without this, a failed refresh triggers the
                // interceptor which calls refresh again, ad infinitum.
                const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

                if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
                    originalRequest._retry = true;

                    // Part 2: single-flight — concurrent 401s share one refresh
                    const refreshed = await refreshTokenSingleFlight();

                    if (refreshed) {
                        const token = getAccessToken();
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return apiClient(originalRequest);
                    }

                    // Part 3: hard redirect on definitive refresh failure.
                    // Use window.location (not navigate) — we're outside React Router.
                    // Part 5: full storage clear (mirrors the logout() reset).
                    const theme = localStorage.getItem('theme');
                    const ref = localStorage.getItem('aime_ref');
                    localStorage.clear();
                    sessionStorage.clear();
                    if (theme) localStorage.setItem('theme', theme);
                    if (ref) localStorage.setItem('aime_ref', ref);
                    setUser(null);

                    if (window.location.pathname !== '/login') {
                        window.location.href = '/login?session_expired=1';
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            apiClient.interceptors.request.eject(requestInterceptor);
            apiClient.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        register,
        updateProfile,
        refreshToken,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook for using auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
